import MUITextField from '@mui/material/TextField';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Select,
  MenuItem,
  Box,
  Checkbox,
  FormLabel,
  styled,
  Divider,
  Pagination,
  InputAdornment,
  IconButton,
  useTheme,
  Tooltip,
  Typography,
} from '@mui/material';
import TextButton from '@product-framework/components/TextButton';
import { SkeletonList } from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import MapLib from '@product-framework/pages/ServiceJob/components/Map/lib/MapLib';
import DispatchEventStatus from '@product-framework/components/DispatchEventStatus';
import { IconLibrary } from '@product-framework/index';
import MultiValueDisplay from '@ui-framework/helpers/MultiValueDisplay';
import formatDateTime from '@product-framework/utils/formatDateTime';
import ReadMoreText from '@ui-framework/components/UI/ReadMoreText';

const Circle = styled('div')(({ theme }) => ({
  borderRadius: '15px',
  height: '30px',
  width: '30px',
  border: 'none',
  background: theme.palette.primary.main,
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  cursor: 'pointer',
}));

export default function Filter({
  spLoading,
  mapRef,
  serviceProviders,
  setSearchText,
  setDispatchEventStatus,
  setPagination,
  searchText,
  dispatchEventStatus,
  pagination,
  dispatchStatusConfig,
}) {
  const theme = useTheme();

  const handleCheck = (checked, item, index) => {
    MapLib.serviceProviderToggleMarker(item?.id, item?.addresses, checked);
    mapRef.current.serviceProviderServiceJobToggleMarker(checked, index);
  };

  const getPageCount = (total, limit) => Math.ceil(total / limit);

  const pageCount = getPageCount(pagination?.total, pagination?.limit);

  const fetchConditions = item => {
    const data = [];
    if (item?.dispatchEvent?.serviceDate) {
      data.push(
        <MultiValueDisplay title="Service Date:">
          {formatDateTime(
            item?.dispatchEvent?.serviceDate,
            item?.dispatchEvent?.serviceDateAfterHour,
            item?.dispatchEvent?.serviceDateBeforeHour,
          )}
        </MultiValueDisplay>,
      );
    }
    if (item?.dispatchEvent?.additionalMoney > 0) {
      data.push(
        <MultiValueDisplay
          type="currency"
          title="Additional Money:"
          sx={{ mb: 1 }}
          color="#ffffff">
          {item?.dispatchEvent?.additionalMoney}
        </MultiValueDisplay>,
      );
    }
    if (item?.dispatchEvent?.reason) {
      data.push(
        <MultiValueDisplay title="Reason:" sx={{ mb: 1 }}>
          <ReadMoreText maxChars={250} allowExpand={false}>
            {item?.dispatchEvent?.reason}
          </ReadMoreText>
        </MultiValueDisplay>,
      );
    }
    if (item?.dispatchEvent?.acceptedLocation?.locationName) {
      data.push(
        <MultiValueDisplay title="Accepted Location:">
          {item?.dispatchEvent?.acceptedLocation?.locationName}
        </MultiValueDisplay>,
      );
    }
    return data?.map(d => <Box sx={{ my: 1 }}>{d}</Box>);
  };

  return (
    <Box sx={{ mt: -1.5, px: 1 }}>
      <Grid2 container spacing={1}>
        <Grid2 container spacing={1}>
          <Grid2 xs={12} md={6}>
            <Tooltip
              title="Minimum 2 characters are needed"
              open={searchText.length === 1}
              arrow>
              <MUITextField
                sx={{ mt: 0.5 }}
                fullWidth
                placeholder="Search by Name"
                size="small"
                value={searchText}
                onChange={e => {
                  setSearchText(e.target?.value);
                }}
                InputProps={{
                  endAdornment: searchText && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear text"
                        onClick={() => {
                          setSearchText('');
                        }}
                        edge="end"
                        size="small">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Tooltip>
          </Grid2>
          <Grid2 xs={12} md={6}>
            <Select
              defaultValue="ALL"
              fullWidth
              name="dispatchStatus"
              size="small"
              value={dispatchEventStatus}
              onChange={e => {
                setDispatchEventStatus(e.target?.value);
              }}>
              {dispatchStatusConfig.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid2>
        </Grid2>
        <Grid2 xs={12}>
          <Divider />
        </Grid2>
        {spLoading && (
          <Grid2 xs={12}>
            <SkeletonList>processing...</SkeletonList>
          </Grid2>
        )}
        {!spLoading && (
          <Grid2 xs={12}>
            {serviceProviders.map((item, index) => (
              <Box key={item.id} sx={{ marginBottom: '10px' }}>
                <Grid2 container spacing={1} alignItems="center">
                  <Grid2>
                    <Circle
                      onClick={() => {
                        const address = item?.addresses?.[0];
                        if (address.locationLat && address.locationLng) {
                          mapRef.current.jumpTo(
                            address.locationLat,
                            address.locationLng,
                          );
                        }
                      }}>
                      {index + 1}
                    </Circle>
                  </Grid2>
                  <Grid2 xs={9}>
                    <TextButton
                      onClick={() => {
                        const address = item?.addresses?.[0];
                        if (address.locationLat && address.locationLng) {
                          mapRef.current.jumpTo(
                            address.locationLat,
                            address.locationLng,
                          );
                        }
                      }}
                      variant="text">{`${item.name}`}</TextButton>
                    <br />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DispatchEventStatus
                        showAsText
                        small
                        value={item.dispatchEvent?.status}
                      />
                      {item.dispatchEvent?.status ===
                        'ACCEPTED_WITH_CONDITIONS' && (
                        <Tooltip
                          title={
                            <Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  mb: 1,
                                }}>
                                <Typography sx={{ textAlign: 'center' }}>
                                  Conditions For Acceptance
                                </Typography>
                                <Divider
                                  sx={{
                                    width: '100%',
                                    my: 1,
                                    borderColor: '#ffffff',
                                  }}
                                />
                              </Box>
                              {fetchConditions(item)}
                            </Box>
                          }
                          arrow>
                          <IconLibrary.MoreInfo color="info" />
                        </Tooltip>
                      )}
                    </Box>
                  </Grid2>
                  <Grid2 xs="grow" textAlign="right">
                    <Checkbox
                      defaultChecked
                      name="serviceProvider"
                      value={item.name}
                      onChange={e => handleCheck(e.target.checked, item, index)}
                    />
                  </Grid2>
                </Grid2>
              </Box>
            ))}
            {serviceProviders.length === 0 && (
              <FormLabel sx={{ color: theme.palette.error.main }}>
                No Service Providers Found
              </FormLabel>
            )}
            <Box>
              {!spLoading && pageCount > 1 && (
                <Pagination
                  page={pagination?.page}
                  onChange={async (event, value) => {
                    setPagination({
                      ...pagination,
                      page: value,
                    });
                  }}
                  sx={{
                    mt: 1,
                    mr: 1,
                    ml: 1,
                    justifyContent: 'flex-end',
                    display: 'flex',
                  }}
                  count={pageCount}
                  size="small"
                />
              )}
            </Box>
          </Grid2>
        )}
      </Grid2>
    </Box>
  );
}
